import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getSecondsLeftUntilExpiry } from '../utils/teeSlotCapacity';

interface Props {
    expiresAt?: unknown;
    expiration?: string | null;
    resID: string;
    deleteGroup: () => void;
    /** When true, the hold timer does not delete the reservation (e.g. during checkout). */
    disableAutoDelete?: boolean;
    /** Seconds before expiration to stop the clock and auto-delete. Default 0. */
    expireBufferSeconds?: number;
}

const CountDown: React.FC<Props> = ({
    expiresAt,
    expiration,
    resID,
    deleteGroup,
    disableAutoDelete = false,
    expireBufferSeconds = 0,
}) => {
    const [secondsLeft, setSecondsLeft] = useState(() =>
        getSecondsLeftUntilExpiry(expiresAt, expiration, expireBufferSeconds)
    );
    const [expired, setExpired] = useState(false);
    const deletedRef = useRef(false);
    const deleteGroupRef = useRef(deleteGroup);
    deleteGroupRef.current = deleteGroup;

    const refreshSecondsLeft = useCallback(() => {
        setSecondsLeft(getSecondsLeftUntilExpiry(expiresAt, expiration, expireBufferSeconds));
    }, [expiresAt, expiration, expireBufferSeconds]);

    useEffect(() => {
        deletedRef.current = false;
        setExpired(false);
        refreshSecondsLeft();
    }, [refreshSecondsLeft, resID]);

    useEffect(() => {
        const intervalId = window.setInterval(refreshSecondsLeft, 1000);
        return () => window.clearInterval(intervalId);
    }, [refreshSecondsLeft]);

    useEffect(() => {
        if (
            !Number.isFinite(secondsLeft) ||
            secondsLeft > 0 ||
            disableAutoDelete ||
            deletedRef.current
        ) {
            return;
        }
        deletedRef.current = true;
        setExpired(true);
        deleteGroupRef.current();
    }, [secondsLeft, disableAutoDelete]);

    function fancyTimeFormat(duration: number) {
        const safe = Number.isFinite(duration) ? duration : 0;
        const hrs = ~~(safe / 3600);
        const mins = ~~((safe % 3600) / 60);
        const secs = ~~safe % 60;
        let ret = '';
        if (hrs > 0) {
            ret += '' + hrs + ':' + (mins < 10 ? '0' : '');
        }
        ret += '' + mins + ':' + (secs < 10 ? '0' : '');
        ret += '' + secs;
        return ret;
    }

    if (expired) {
        return (
            <div
                className="alert alert-danger mb-0 text-center rounded-0"
                id="bookingAlert"
                role="alert"
                style={{ lineHeight: 1 }}
            >
                <div id="timer">Time expired. Please choose a time below.</div>
            </div>
        );
    }

    return (
        <div
            className="alert alert-warning mb-0 text-center rounded-0"
            id="bookingAlert"
            role="alert"
            style={{ lineHeight: 1 }}
        >
            <div id="timer">Time Left: {fancyTimeFormat(secondsLeft)}</div>
        </div>
    );
};

export default CountDown;
